import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Pencil, RotateCcw, WifiOff } from "lucide-react";
import { AiOrb } from "../../../shared/ui/ai-orb";
import { voteAnswer, type AiMatch, type Itinerary } from "../api/ai.api";
import type { ChatLang, ChatMessage } from "../hooks/use-ai-chat";
import { AnswerSections, unnamedMatches } from "./answer-sections";
import { isSpaCategory, resultNumbers, shouldShowResultsMap } from "@majourneys/shared";
import { ChatResultsMap } from "./chat-results-map";
import { BudgetView } from "./budget-view";
import { DocProse } from "./doc-prose";
import {
  EditorialAdvice,
  EditorialHeader,
  EditorialSections,
  EstimateBlock,
  LocalKnowledge,
} from "./editorial";
import { ClarifyLinks } from "./follow-up-chips";
import { CompareView } from "./compare-view";
import { PlaceView } from "./place-view";
import { BookingProposalCard } from "./booking-proposal-card";
import { RideView } from "./ride-view";
import { StaysList } from "./stays-list";
import { hasSections, looksLikeDoc } from "../lib/doc-markdown";
import { ItineraryView } from "./itinerary-view";
import { MatchGrid } from "./match-card";
import { RADIUS, SOFT, TURN_ORB_SIZE } from "./soft-tokens";
import { ThinkingIndicator } from "./thinking-indicator";
import { AnswerActions, type AnswerVoteInput } from "./answer-actions";
import { useRequestSent } from "../lib/proposal-status";
import { useBucketOptional } from "../../bucket/state/bucket-context";
import {
  AddedNote,
  ChoiceAsk,
  DatePartyAsk,
  SpaGuestMix,
  TalkToTeam,
  TimeSlots,
  TripListCard,
} from "./kit";
import { isRestaurantRef } from "./layout-parts";

export type AssistantMessage = Extract<ChatMessage, { kind: "assistant" }>;

/**
 * The traveller's own message (chat kit C2): the navy ramp on the end side.
 *
 * With `onEdit`, a pencil sits beside it (on hover or focus, always on touch
 * screens, 28px so the hit area clears 24). It hands the text back to the
 * composer; the host then sends it with `replaceFrom`, which cuts the thread
 * at this message and asks again.
 */
export function UserBubble({
  text,
  id,
  onEdit,
}: {
  text: string;
  id?: string;
  onEdit?: () => void;
}) {
  const { t } = useTranslation();
  return (
    // `data-turn` is what useThreadAnchor scrolls to: the question goes to the
    // top of the thread so the answer under it reads from its first line.
    <div className="mj-row-in group flex items-end justify-end gap-1.5" data-turn={id}>
      {onEdit ? (
        <button
          type="button"
          onClick={onEdit}
          aria-label={t("ai_concierge.turn.edit", { defaultValue: "Edit message" })}
          className="grid h-7 w-7 shrink-0 place-items-center rounded-full opacity-0 transition-opacity focus-visible:opacity-100 group-hover:opacity-100 group-focus-within:opacity-100 [@media(hover:none)]:opacity-100"
          style={{ color: SOFT.body }}
          data-testid="edit-message"
        >
          <Pencil size={13} aria-hidden="true" />
        </button>
      ) : null}
      <div
        className="max-w-[85%] px-3.5 py-2.5 text-[15.5px] font-medium leading-[21px] text-white"
        style={{
          background: SOFT.inkGradient,
          borderRadius: RADIUS.bubble,
          borderEndEndRadius: 6,
        }}
      >
        {text}
      </div>
    </div>
  );
}

/** A turn still running: the orb working beside the agent's narration. */
export function PendingTurn({ steps }: { steps?: string[] }) {
  return (
    <div className="mj-row-in self-start">
      <ThinkingIndicator steps={steps} />
    </div>
  );
}

/**
 * Who is speaking, drawn once at the top of an assistant turn.
 *
 * Phone ruling: a left gutter was the wrong shape here. The card grid below
 * is deliberately full-bleed, and indenting every turn to clear an avatar
 * column would crop it or leave it hanging off a gutter nothing else
 * respects. So the bead sits ABOVE the content as a signature rather than
 * beside it as a column. Unlabelled on purpose: the header already says
 * MaJourneys AI, and the bead is the same object the traveller just watched
 * think.
 */
function TurnAvatar() {
  return (
    <div className="mb-2.5">
      <AiOrb size={TURN_ORB_SIZE} />
    </div>
  );
}

/**
 * One finished assistant turn, shared by the homepage modal and the Discover
 * panel (ai-chat-parity rule: the two are one product surface).
 *
 * Shapes, in order of precedence: clarify (prose plus gold answer words),
 * itinerary, budget, the ride / place / compare layouts, or prose plus the
 * card grid. A turn's own follow-ups hang off the turn that asked the
 * question, never off the composer.
 */
export function AssistantTurn({
  message,
  lang,
  inTrip,
  onAdd,
  onAddItinerary,
  activeTripName,
  onRefine,
  onVote,
  shareSessionId,
  conversationId,
  onCompose,
  isLatest = false,
  teamShareUrl,
}: {
  message: AssistantMessage;
  lang: ChatLang;
  inTrip: Set<string>;
  onAdd: (id: string) => void;
  onAddItinerary?: (itinerary: Itinerary, matches: AiMatch[], mode: "new" | "active") => void;
  activeTripName?: string | null;
  /** Sends one of this turn's own one-tap answers. */
  onRefine: (query: string) => void;
  /**
   * Files a thumbs up or down (chat kit C29). Defaults to `voteAnswer`; a
   * host or a test may pass its own. Without the turn's `turnId`, no thumbs.
   */
  onVote?: (input: AnswerVoteInput) => void | Promise<unknown>;
  /** The saved conversation's local id, filed with a vote. */
  conversationId?: string | null;
  /** The saved chat's id, when signed in: enables Share under the answer. */
  shareSessionId?: string | null;
  /** Puts text in the composer, focused, without sending it ("Change"). */
  onCompose?: (text: string) => void;
  /**
   * The last thing in the thread AND answered in this session. Only then are
   * the one-tap asks, pickers and times live; anywhere else (an older turn,
   * a chat reloaded from history) they render locked, so nothing re-sends.
   */
  isLatest?: boolean;
  /** The chat's share link, when the host has one, for the WhatsApp handoff. */
  teamShareUrl?: string | null;
}) {
  const { t } = useTranslation();
  const sent = useRequestSent(message.id, message.bookingProposal, message.requestSent);
  /**
   * C26: one "Added to your trip · Undo" per add from this turn's cards. The
   * key is a counter so a second add restarts the note rather than reusing
   * the one already fading out.
   */
  const [added, setAdded] = useState<{ id: string; n: number } | null>(null);
  const bucket = useBucketOptional();
  const addFromTurn = (id: string) => {
    const adding = !inTrip.has(id);
    onAdd(id);
    setAdded(adding ? { id, n: (added?.n ?? 0) + 1 } : null);
  };
  const isItinerary = !message.clarify && message.type === "itinerary" && !!message.itinerary;
  const isBudget = !message.clarify && message.type === "budget" && !!message.budget;
  /**
   * The three layouts decided on the finished answer: a ride card, one place,
   * or a side-by-side. Each IS the answer's structure, so the voice header,
   * the shelves, the loose card grid, the map and the advice slots all stand
   * down: a place card under a headline and above a grid of the same place
   * says it three times.
   */
  const layout = message.clarify
    ? undefined
    : message.type === "ride" && message.ride
      ? "ride"
      : message.type === "place" && message.place
        ? "place"
        : message.type === "compare" && message.compare
          ? "compare"
          : undefined;
  /**
   * NO SUGGESTED QUESTIONS. Owner rule, 2026-09-08: "dont ever do
   * suggestions."
   *
   * The API still sends `followUps` — the model's own next questions — and we
   * deliberately render none of them. A stack of questions under every answer
   * is the interface writing the traveller's next line for them, and it grew
   * back three times: once above the composer, once as a generic rail, once as
   * "Keep shaping it" under the turn.
   *
   * `suggestions` are NOT the same thing and stay: they answer a question the
   * assistant just asked ("which city?"), so removing them would leave a
   * clarifying turn with no way forward at all.
   */
  const suggestions = message.suggestions ?? [];
  const text = message.text.trim();
  /**
   * A DOCUMENT REPLACES THE BUBBLE ENTIRELY.
   *
   * The answer arrives as markdown — `##` sections, `- bullets`, `**bold**`,
   * `[[slug|Label]]` names — and printing it into a chat bubble ran fifteen
   * bulleted lines together into one paragraph and showed the raw entity
   * syntax. A document is the layout; wrapping it in a bubble fights its own
   * sections. Phone ruling, assistant-turn.tsx.
   */
  const isDoc =
    !message.clarify && !!text && (message.format === "markdown" || looksLikeDoc(text));
  const sectioned = isDoc && hasSections(text);
  /**
   * An editorial answer carries its own structure: the voice header, themed
   * shelves with a line per place, the estimate, the advice slots.
   *
   * THE SECTIONS ARE THE ANSWER, so the prose above them is a second copy of
   * it — the voice pass writes the same shelves twice, once as `sections` and
   * once flattened into the document. Phone ruling, assistant-turn.tsx: where
   * a structure carries the answer, the document is noise.
   */
  const editorial = message.clarify || layout ? undefined : message.editorial;
  const editorialSections = editorial?.sections?.length ? editorial.sections : undefined;
  const structured = !!editorialSections && message.matches.length > 0;
  /**
   * THE CARDS WAIT FOR THE WORDS (phone ruling, assistant-turn.tsx).
   *
   * Matches land seconds before the prose does, and drawing them first put a
   * row of results above a sentence nobody had written yet: the traveller is
   * handed things to tap while still being told to wait. The itinerary is the
   * deliberate exception below — it IS the answer's structure and streams from
   * the first frame.
   */
  const showCards =
    !!text &&
    !message.clarify &&
    !isItinerary &&
    !isBudget &&
    !layout &&
    !structured &&
    message.matches.length > 0;
  // A sectioned answer hands each section the rows its own prose names; only
  // what it never named is shown once at the end, rather than dropped.
  const gridMatches = sectioned ? unnamedMatches(text, message.matches) : message.matches;
  /**
   * Whether a results map is drawn above the cards. The number badges only
   * make sense as a key to its pins, so without the map they carry none.
   */
  const mapped = shouldShowResultsMap(message.matches);
  /**
   * Which pin each card is, keyed by slug, or nothing when there is no map.
   *
   * Keyed by slug rather than by position because every card surface below
   * shows a different slice: a sectioned answer hands each section the rows
   * its own prose names, and the grid gets only what was never named. An
   * earlier revision passed a boolean and numbered by local position, which
   * meant the section paths silently carried no numbers at all — measured on
   * production, twenty pins and not one card badge.
   */
  const numbers = mapped ? resultNumbers(message.matches) : undefined;

  /**
   * CHIPS ONLY WHEN THE ASSISTANT ASKS (owner rule): a choice ask, or the two
   * travel questions an off-topic refusal offers. Never under an answer.
   */
  const ask = message.ask;
  const locked = !isLatest || !!message.streaming;
  const askMatch =
    ask?.kind === "date_party" && ask.slug
      ? message.matches.find((m) => m.slug === ask.slug)
      : undefined;
  const spaAsk = !!askMatch && isSpaCategory(askMatch.categorySlug);
  const timesMatch = message.times
    ? message.matches.find((m) => m.slug === message.times!.slug)
    : undefined;
  /**
   * C28 on a dead end: the assistant asked something back with nothing to
   * tap and nothing found. A question with one-tap answers is still helping,
   * so those keep their answers and no handoff.
   */
  const couldNotHelp =
    isLatest &&
    !!text &&
    !message.streaming &&
    !!message.clarify &&
    !ask &&
    !message.offTopic &&
    message.matches.length === 0 &&
    suggestions.length === 0;

  return (
    <div className="min-w-0">
      {/* ONE BEAD PER TURN. While the turn is still writing, its wait
          indicator already carries the orb — at the same size and the same
          place — so drawing the avatar as well put two beads on one answer,
          one above the other. The thinking orb IS the avatar until the text
          lands, then this one takes over at idle speed. Phone ruling,
          assistant-turn.tsx. */}
      {text || isItinerary || isBudget ? <TurnAvatar /> : null}

      {/* Streamed turns arrive as matches first and prose several seconds
          later, so a turn with no text yet is a real state rather than a bug.
          Say what is happening instead of showing an empty box. */}
      {!text && !isItinerary && !isBudget ? (
        <ThinkingIndicator stage="writing" steps={message.steps} />
      ) : null}

      {/* Editorial turns open with the voice header; the prose below it is
          page copy, not a bubble — the header already owns the layout. */}
      {editorial ? <EditorialHeader block={editorial} /> : null}

      {/* Under the header, above the cards: the cards are things you can book
          at a real price, and this is arithmetic about everything else. Held
          back until the answer has landed, for the same reason the cards are. */}
      {text && editorial?.estimate ? <EstimateBlock estimate={editorial.estimate} /> : null}

      {/* THE PLAN IS THE ANSWER, so its prose is a second copy of it. The
          model writes the same days twice — once as `itinerary`, which renders
          below as a timeline, and once flattened into the document. The reader
          got the whole plan as a paragraph and then again with pictures. */}
      {text && isDoc && !isItinerary && !structured ? (
        sectioned ? (
          <AnswerSections
            text={text}
            matches={message.matches}
            lang={lang}
            inTrip={inTrip}
            onAdd={addFromTurn}
            numbers={numbers}
          />
        ) : (
          <DocProse text={text} matches={message.matches} />
        )
      ) : text && !isDoc && !isItinerary && !structured ? (
        // C4: the answer is page copy, not a bubble. Only the guest's own
        // words sit in a shape.
        <p className="text-start text-[14px] leading-[22px]" style={{ color: SOFT.ink }}>
          {text}
          {isBudget ? (
            <span style={{ color: SOFT.body }}>
              {" · "}
              {t("ai_concierge.budget_meta", {
                defaultValue: "{{picks}} picks",
                picks: message.budget!.items.filter((i) => i.kept).length,
              })}
            </span>
          ) : null}
        </p>
      ) : null}

      {/* A structured ask carries the same answers as `suggestions` (the API
          builds one from the other), so the links stand down when it is here. */}
      {message.clarify && !ask ? (
        <ClarifyLinks items={suggestions} onPress={onRefine} />
      ) : null}

      {ask?.kind === "choice" && !message.streaming ? (
        <div className="mt-3">
          <ChoiceAsk
            question={ask.question}
            options={ask.options}
            onSend={onRefine}
            showQuestion={!message.offTopic && !text}
            locked={locked}
          />
        </div>
      ) : null}

      {text && ask?.kind === "date_party" ? (
        <div className="mt-3 flex flex-col gap-2">
          <DatePartyAsk
            question={ask.question}
            needs={spaAsk ? ["date"] : ask.needs}
            lang={lang}
            onSend={onRefine}
            locked={locked}
          />
          {spaAsk ? (
            <SpaGuestMix
              slug={askMatch!.slug}
              venueName={askMatch!.title ?? askMatch!.slug}
              lang={lang}
              onSend={onRefine}
              locked={locked}
            />
          ) : null}
        </div>
      ) : null}

      {text && message.times ? (
        <div className="mt-3">
          <TimeSlots
            times={message.times}
            placeName={timesMatch?.title}
            isRestaurant={isRestaurantRef(timesMatch)}
            lang={lang}
            onSend={onRefine}
            locked={locked}
          />
        </div>
      ) : null}

      {couldNotHelp ? (
        <div className="mt-3">
          <TalkToTeam shareUrl={teamShareUrl} />
        </div>
      ) : null}

      {isItinerary ? (
        <div className="mt-3">
          <ItineraryView
            itinerary={message.itinerary!}
            matches={message.matches}
            lang={lang}
            activeTripName={activeTripName}
            onAddToTrip={
              onAddItinerary
                ? (mode) => onAddItinerary(message.itinerary!, message.matches, mode)
                : undefined
            }
          />
        </div>
      ) : null}

      {isBudget ? (
        <div className="mt-3">
          <BudgetView budget={message.budget!} matches={message.matches} />
        </div>
      ) : null}

      {/* Above every card surface, never over one: the map says WHERE and the
          cards say WHAT. It waits for the words for the same reason the
          shelves below do. Draws nothing unless it can place most of the
          answer — see `shouldShowResultsMap`. */}
      {layout === "ride" ? (
        <div className="mt-3">
          <RideView ride={message.ride!} lang={lang} />
        </div>
      ) : layout === "place" ? (
        <div className="mt-3">
          <PlaceView layout={message.place!} lang={lang} />
        </div>
      ) : layout === "compare" ? (
        <div className="mt-3">
          <CompareView layout={message.compare!} lang={lang} />
        </div>
      ) : null}

      {/* The booking the assistant put together, to confirm on the place's own
          form. Waits for the words, which say it is proposed below. */}
      {text && message.bookingProposal ? (
        <div className="mt-3">
          <BookingProposalCard
            proposal={message.bookingProposal}
            lang={lang}
            messageId={message.id}
            sent={sent}
            onChange={
              onCompose
                ? () =>
                    onCompose(
                      t("ai_concierge.proposal.change_prompt", {
                        title: message.bookingProposal!.title,
                      }),
                    )
                : undefined
            }
          />
        </div>
      ) : null}

      {/* Somewhere to sleep, when the assistant looked for one. */}
      {text && message.stays && message.stays.items.length > 0 ? (
        <div className="mt-3">
          <StaysList shelf={message.stays} lang={lang} />
        </div>
      ) : null}

      {text && !message.clarify && !layout ? (
        <div className="mt-3">
          <ChatResultsMap matches={message.matches} lang={lang} />
        </div>
      ) : null}

      {/* The shelves wait for the words too — a themed section above a
          sentence nobody has written yet is the same failure as a bare grid. */}
      {text && structured ? (
        <EditorialSections
          sections={editorialSections!}
          matches={message.matches}
          lang={lang}
          inTrip={inTrip}
          onAdd={addFromTurn}
          why={editorial?.why}
          numbers={numbers}
        />
      ) : null}

      {showCards ? (
        <div className="mt-3">
          <MatchGrid
            matches={gridMatches}
            lang={lang}
            inTrip={inTrip}
            onAdd={addFromTurn}
            numbers={numbers}
          />
        </div>
      ) : null}

      {/* C26 and C27: the add lands with an Undo, and the trip it landed in
          shows under it (TripListCard draws nothing while the bucket is empty;
          without a bucket on the page there is no trip to show). */}
      {added ? (
        <div className="mt-3 flex flex-col gap-2">
          <AddedNote
            key={added.n}
            onUndo={() => {
              onAdd(added.id);
              setAdded(null);
            }}
          />
          {bucket ? <TripListCard lang={lang} /> : null}
        </div>
      ) : null}

      {text && editorial ? <EditorialAdvice block={editorial} /> : null}

      {text && editorial?.localKnowledge?.length ? (
        <LocalKnowledge items={editorial.localKnowledge} />
      ) : null}

      {message.stopped ? (
        <p className="mt-2 text-start text-[12px]" style={{ color: SOFT.body }} data-testid="answer-stopped">
          {t("ai_concierge.turn.stopped", { defaultValue: "You stopped this answer." })}
        </p>
      ) : null}

      {/* Under a finished answer only: never while it is still arriving, and
          never under a question back or an off-topic refusal, which are not
          answers to rate. No suggested next questions here (owner rule). */}
      {text && !message.streaming && !message.clarify ? (
        <AnswerActions
          text={text}
          turnId={message.turnId}
          question={message.query}
          lang={lang}
          onVote={
            onVote ??
            ((v) =>
              voteAnswer(v.turnId, {
                vote: v.vote === "up" ? 1 : -1,
                question: v.question.slice(0, 1000),
                answer: v.answer.slice(0, 4000),
                lang: v.lang,
                ...(v.reason ? { reason: v.reason } : {}),
                ...(conversationId ? { conversationId } : {}),
              }))
          }
          shareSessionId={shareSessionId}
        />
      ) : null}
    </div>
  );
}

/**
 * C17: the answer did not arrive. A red-tinted row, plain words, one way back.
 *
 * Always the translated sentence, never the error it stands for. It used to
 * print the thrown message, which is written for developers: a long chat once
 * showed a traveller "ai/chat/stream 422: {\"error\":{\"code\":...". The detail
 * goes to the console from useAiChat instead. The guest's message stays in the
 * thread above it, and Try again asks it again in place.
 */
export function ChatError({
  onRetry,
  offerTeam = false,
  shareUrl,
}: {
  onRetry?: () => void;
  /** Try again already failed once: offer a person (C28) under the row. */
  offerTeam?: boolean;
  shareUrl?: string | null;
}) {
  const { t } = useTranslation();
  const row = (
    <div
      className="mj-row-in flex items-center gap-3 self-stretch rounded-[16px] px-3 py-2.5"
      style={{ background: "rgba(190,60,60,0.08)" }}
      role="alert"
    >
      <WifiOff size={16} color="#9B2C2C" aria-hidden="true" className="shrink-0" />
      <span className="min-w-0 flex-1 text-start text-[12.5px]" style={{ color: "#9B2C2C" }}>
        {t("ai_concierge.turn.error", {
          defaultValue: "That answer did not arrive. Your message is kept.",
        })}
      </span>
      {onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          className="mj-press inline-flex min-h-[32px] shrink-0 items-center gap-1.5 rounded-full px-3 text-[12px] font-semibold"
          style={{ background: SOFT.controlFill, boxShadow: `inset 0 0 0 1px ${SOFT.controlEdge}`, color: SOFT.ink }}
        >
          <RotateCcw size={12} aria-hidden="true" />
          {t("ai_concierge.retry", { defaultValue: "Try again" })}
        </button>
      ) : null}
    </div>
  );
  if (!offerTeam) return row;
  return (
    <div className="flex flex-col gap-2 self-stretch">
      {row}
      <TalkToTeam shareUrl={shareUrl} />
    </div>
  );
}
